import { SessionAPIResponse, TelSysAdminDTO } from '../../../NonReact/Types/Types'
import { COMMA, DEFAULT_PASSWORD_VALUE, EMPTY_STRING } from '../../../NonReact/constants/Common'
import { RecordingIntegrations } from '../../Common/Constants/Recordings'
import { DEFAULT_CME_ID } from '../../Common/Constants/ServerValues'
import { StateSetter } from '../../Common/Types/Types'
import {
    BusinessOption,
    DEFAULT_COUNTRY_CODE,
    DEFAULT_ENTERPRISE_USER_EXTENSIONS,
    DEFAULT_INTERNAL_DIGIT_LENGTH,
    DEFAULT_MAX_PARTITION_COUNT,
    DEFAULT_MAX_USERS_MONITORED,
    DEFAULT_MAX_USERS_SYNC,
    DEFAULT_TIMEZONE,
    INTEGRATION_MODE_HISTORIC,
    INTEGRATION_MODE_REAL_TIME,
    INTEGRATION_TYPE,
    IntegrationType,
    LicencedForRptTypes,
    TelSysTypes
} from '../Constants/Constants'
import { TelSysForm, TelSysLicenceManagement } from '../Types/Types'
import { encrypt } from '../../Common/Utils/Security'
import { getSession } from '../../Common/API/CommonAPI'
import { IsStringEmpty, isStringNotEmpty, IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

type MapFromServerToFormFn = (
    telSys: JSONResponse<TelSysAdminDTO>,
    setCommsStatus: StateSetter<Nullable<string[]>>,
    setSyncStatus: StateSetter<Nullable<string>>
) => TelSysForm

export const mapFromServerToForm: MapFromServerToFormFn = (
    telSys: JSONResponse<TelSysAdminDTO>,
    setCommsStatus: StateSetter<Nullable<string[]>>,
    setSyncStatus: StateSetter<Nullable<string>>
): TelSysForm => {
    setCommsStatus(telSys?.CommsLogs ?? undefined)
    setSyncStatus(telSys?.SyncStatus ?? undefined)

    return {
        details: {
            id: `${telSys?.TelSysID ?? EMPTY_STRING}`,
            description: telSys?.Description ?? EMPTY_STRING,
            cme: telSys?.CMEnvironmentID ?? DEFAULT_CME_ID,
            tags: telSys?.Tags ?? EMPTY_STRING,
            billingID: telSys?.BillingTag ?? EMPTY_STRING,
            billable: !!telSys?.Billable,
            trial: telSys?.RemainingTrialPeriod ?? 0,
            eccrReplacement: !!telSys?.ECCRReplacement,
            aiAssistEnabled: !!telSys?.AIAssistEnabled,
            mfa: !!telSys?.ManageUserMFA
        },
        communications: {
            type: (telSys?.TelSysType as Definable<TelSysTypes>) ?? TelSysTypes.UNKNOWN,
            msTeamsModeType: telSys?.HistoricOnly
                ? INTEGRATION_MODE_HISTORIC
                : INTEGRATION_MODE_REAL_TIME ?? INTEGRATION_MODE_REAL_TIME,
            integrationMode: telSys?.IntegrationMode ?? undefined,
            integrationType: (telSys?.IntegrationType as IntegrationType) ?? INTEGRATION_TYPE.LEGACY_TEAMS,
            businessOptions: isStringNotEmpty(telSys?.IntegrationModeOptions)
                ? (telSys?.IntegrationModeOptions?.split(COMMA) as BusinessOption[])
                : undefined,
            simulationMode: telSys?.CallControlOptions ?? EMPTY_STRING,
            simulateOmnichannelActivity: !!telSys?.OmniChannelPermitted,
            licencedFor: telSys?.LicensedForRptUserType ?? LicencedForRptTypes.TWO_THOUSAND,
            ipAddress1: telSys?.IPAddrOrHost1 ?? EMPTY_STRING,
            ipAddress2: telSys?.IPAddrOrHost2 ?? EMPTY_STRING,
            tcpPort1: telSys?.TCPPort1 ?? undefined,
            userName1: telSys?.AuthUserID1 ?? EMPTY_STRING,
            password1: telSys?.AuthPassword1Provided ? DEFAULT_PASSWORD_VALUE : EMPTY_STRING,
            tcpPort2: telSys?.TCPPort2 ?? undefined,
            useSSLTLS2: telSys?.UseSSLTLS2 ?? false,
            userName2: telSys?.AuthUserID2 ?? EMPTY_STRING,
            password2: telSys?.AuthPassword2Provided ? DEFAULT_PASSWORD_VALUE : EMPTY_STRING,
            ipAddress3: telSys?.IPAddrOrHost3 ?? EMPTY_STRING,
            userName3: telSys?.AuthUserID3 ?? EMPTY_STRING,
            password3: telSys?.AuthPassword3Provided ? DEFAULT_PASSWORD_VALUE : EMPTY_STRING,
            ipAddress4: telSys?.IPAddrOrHost4 ?? EMPTY_STRING,
            tcpPort4: telSys?.TCPPort4 ?? undefined,
            useSSLTLS4: telSys?.UseSSLTLS4 ?? false,
            userName4: telSys?.AuthUserID4 ?? EMPTY_STRING,
            password4: telSys?.AuthPassword4Provided ? DEFAULT_PASSWORD_VALUE : EMPTY_STRING,
            xspServer: telSys?.XSPServer ?? EMPTY_STRING,
            commsParameterText1: telSys?.CommsParameter1 ?? EMPTY_STRING,
            commsParameterText2: telSys?.CommsParameter2 ?? EMPTY_STRING,
            commsEnabled: !!telSys?.CommsEnabled,
            inService: !!telSys?.MonitoringEnabled,
            cpMonitoredCQEnabled: !!telSys?.CPMonitoredCQEnabled,
            syncEnvOnSetup: telSys?.SyncEnvironmentType
        },
        sync: {
            syncNow: false,
            lastSync: telSys?.LastSyncAt ?? EMPTY_STRING
        },
        defaultSiteSettings: {
            timezone: telSys?.TimeZone ?? DEFAULT_TIMEZONE,
            countryCode: telSys?.CountryCode ?? DEFAULT_COUNTRY_CODE,
            internalDigitLength: telSys?.InternalDigitLen ?? DEFAULT_INTERNAL_DIGIT_LENGTH,
            outboundTelNoPrefix: telSys?.TelNoPrefixes ?? EMPTY_STRING,
            recordingIntegration: telSys?.CallRecIntIDDefault ?? RecordingIntegrations.NONE,
            e164Enabled: telSys?.E164Enabled ?? true
        },
        licenseManagement: {
            essentials:
                (telSys?.LicenseCapManagement?.EssentialsCap ?? ALL_VALUE) === ALL_VALUE
                    ? EMPTY_STRING
                    : `${telSys?.LicenseCapManagement?.EssentialsCap ?? EMPTY_STRING}`,
            professional:
                (telSys?.LicenseCapManagement?.ProfessionalCap ?? ALL_VALUE) === ALL_VALUE
                    ? EMPTY_STRING
                    : `${telSys?.LicenseCapManagement?.ProfessionalCap ?? EMPTY_STRING}`,
            enterprise:
                (telSys?.LicenseCapManagement?.EnterpriseCap ?? ALL_VALUE) === ALL_VALUE
                    ? EMPTY_STRING
                    : `${telSys?.LicenseCapManagement?.EnterpriseCap ?? EMPTY_STRING}`,
            callCentreAgent:
                (telSys?.LicenseCapManagement?.CallCentreAgentCap ?? ALL_VALUE) === ALL_VALUE
                    ? EMPTY_STRING
                    : `${telSys?.LicenseCapManagement?.CallCentreAgentCap ?? EMPTY_STRING}`,
            callCentreSupervisor:
                (telSys?.LicenseCapManagement?.CallCentreSupervisorCap ?? ALL_VALUE) === ALL_VALUE
                    ? EMPTY_STRING
                    : `${telSys?.LicenseCapManagement?.CallCentreSupervisorCap ?? EMPTY_STRING}`,
            cxIntegration:
                (telSys?.LicenseCapManagement?.CXIntegrationCap ?? ALL_VALUE) === ALL_VALUE
                    ? EMPTY_STRING
                    : `${telSys?.LicenseCapManagement?.CXIntegrationCap ?? EMPTY_STRING}`
        },
        advanced: {
            partitionCountMax: telSys?.Advanced?.PartitionCountMax ?? DEFAULT_MAX_PARTITION_COUNT,
            scheduledReportsAllowedPerUser: telSys?.Advanced?.ScheduledReportsPerUser ?? EMPTY_STRING,
            scheduledEmailsAllowedPerReport: telSys?.Advanced?.EmailsPerScheduledReport ?? EMPTY_STRING,
            ctiLogIncClassNames: !!telSys?.Advanced?.CTILogIncClassNames,
            internalDiagDumpEnabled: !!telSys?.Advanced?.InternalDiagDumpEnabled,
            callModelDiagDumpEnabled: !!telSys?.Advanced?.CallModelDiagDumpEnabledForCalls,
            ctiLogEnabled: !!telSys?.Advanced?.CTILogEnabled,
            partitionsRptingScopeAllEnabled: !!telSys?.Advanced?.PartitionsRptingScopeAllEnabled,
            callModelDiagDumpEnabledForDev: telSys?.Advanced?.CallModelDiagDumpEnabledForDev ?? EMPTY_STRING,
            ctiCallModelLogEnabled: !!telSys?.Advanced?.CTICallModelLogEnabled,
            userNotificationsEnabled: !!telSys?.Advanced?.UserNotificationsEnabled,
            extensionsPerEnterpriseUser:
                telSys?.Advanced?.ExtensionsPerEnterpriseUser ?? DEFAULT_ENTERPRISE_USER_EXTENSIONS,
            maximumAllowedUsersSync: telSys?.Advanced?.MaximumAllowedUsersSync ?? DEFAULT_MAX_USERS_SYNC,
            maximumAllowedUsersMonitored: telSys?.Advanced?.MaximumAllowedUsersMonitored ?? DEFAULT_MAX_USERS_MONITORED
        }
    }
}

const getPasswordValue = (password?: string, nonce?: string): OnlyNullable<string> =>
    isStringNotEmpty(password) && password !== DEFAULT_PASSWORD_VALUE ? encrypt(password, nonce) : null

export const mapFromServerLicencesToForm = (
    telSys: JSONResponse<TelSysAdminDTO>
): TelSysLicenceManagement<Nullable<number>> => ({
    essentials: telSys?.LicenseCapManagement?.EssentialsCap,
    professional: telSys?.LicenseCapManagement?.ProfessionalCap,
    enterprise: telSys?.LicenseCapManagement?.EnterpriseCap,
    callCentreAgent: telSys?.LicenseCapManagement?.CallCentreAgentCap,
    callCentreSupervisor: telSys?.LicenseCapManagement?.CallCentreSupervisorCap,
    cxIntegration: telSys?.LicenseCapManagement?.CXIntegrationCap
})

export const mapFromFormToServer = (
    form: TelSysForm,
    hasTelsysSyncRestrictionPerms: boolean
): Promise<TelSysAdminDTO> =>
    getSession().then(
        (session: JSONResponse<SessionAPIResponse>): TelSysAdminDTO => ({
            ...(isStringNotEmpty(form.details.id) ? { TelSysID: parseInt(form.details.id, 10) } : {}),
            Description: form.details.description,
            CMEnvironmentID: form.details.cme,
            Tags: form.details.tags,
            BillingTag: form.details.billingID,
            Billable: form.details.billable,
            TrialEnabled: typeof form.details.trial === 'number' ? null : form.details.trial,
            ManageUserMFA: form.details.mfa,
            TelSysType: form.communications.type ?? TelSysTypes.UNKNOWN,
            OperatingMode:
                form.communications.type === TelSysTypes.MS_TEAMS ? form.communications.msTeamsModeType : undefined,
            HistoricOnly:
                form.communications.type === TelSysTypes.MS_TEAMS
                    ? form.communications.msTeamsModeType === INTEGRATION_MODE_HISTORIC
                    : undefined,
            CPMonitoredCQEnabled: form.communications.cpMonitoredCQEnabled,
            CallControlOptions: form.communications.simulationMode,
            OmniChannelPermitted: form.communications.simulateOmnichannelActivity,
            LicensedForRptUserType: form.communications.licencedFor,
            IntegrationMode: form.communications.integrationMode,
            IntegrationType: form.communications.integrationType,
            IntegrationModeOptions:
                IsVarEmpty(form.communications.businessOptions) ||
                form.communications.integrationType === INTEGRATION_TYPE.LEGACY_TEAMS
                    ? null
                    : form.communications.businessOptions.join(COMMA),
            DeleteACDAgentsOnLicenseDowngrade: IsVarEmpty(form.communications.deleteACDAgents)
                ? null
                : form.communications.deleteACDAgents,
            IPAddrOrHost1: form.communications.ipAddress1,
            IPAddrOrHost2: form.communications.ipAddress2,
            TCPPort1: form.communications.tcpPort1,
            AuthUserID1: form.communications.userName1,
            AuthPassword1: getPasswordValue(form.communications.password1, session?.Nonce),
            TCPPort2: form.communications.tcpPort2,
            UseSSLTLS2: form.communications.useSSLTLS2,
            AuthUserID2: form.communications.userName2,
            AuthPassword2: getPasswordValue(form.communications.password2, session?.Nonce),
            AuthPassword2OAuthSessionID: isStringNotEmpty(form.communications.oauthSessionID)
                ? form.communications.oauthSessionID
                : null,
            IPAddrOrHost3: form.communications.ipAddress3,
            AuthUserID3: form.communications.userName3,
            AuthPassword3: getPasswordValue(form.communications.password3, session?.Nonce),
            IPAddrOrHost4: form.communications.ipAddress4,
            TCPPort4: form.communications.tcpPort4,
            UseSSLTLS4: form.communications.useSSLTLS4,
            AuthUserID4: form.communications.userName4,
            AuthPassword4: getPasswordValue(form.communications.password4, session?.Nonce),
            XSPServer: form.communications.xspServer,
            CommsParameter1: form.communications.commsParameterText1,
            CommsParameter2: form.communications.commsParameterText2,
            CommsEnabled: form.communications.commsEnabled ?? false,
            MonitoringEnabled: form.communications.inService ?? false,
            SyncEnvironmentType: form.communications.syncEnvOnSetup,
            TimeZone: form.defaultSiteSettings.timezone,
            CountryCode: form.defaultSiteSettings.countryCode,
            InternalDigitLen: form.defaultSiteSettings.internalDigitLength,
            TelNoPrefixes: form.defaultSiteSettings.outboundTelNoPrefix,
            CallRecIntIDDefault: form.defaultSiteSettings.recordingIntegration,
            ...(form.communications.type === TelSysTypes.MS_TEAMS
                ? {
                      E164Enabled: form.defaultSiteSettings.e164Enabled
                  }
                : {}),
            SyncNow: form.sync.syncNow,
            AddExtsAgents: form.sync.syncNow,
            ECCRReplacement: form.details.eccrReplacement,
            AIAssistEnabled: form.details.aiAssistEnabled,
            TelSysProfileID: form.communications.telSysProfile,
            Advanced: {
                PartitionCountMax: form.advanced.partitionCountMax,
                CTILogIncClassNames: form.advanced.ctiLogIncClassNames,
                InternalDiagDumpEnabled: form.advanced.internalDiagDumpEnabled,
                CallModelDiagDumpEnabledForCalls: form.advanced.callModelDiagDumpEnabled,
                CTILogEnabled: form.advanced.ctiLogEnabled,
                PartitionsRptingScopeAllEnabled: form.advanced.partitionsRptingScopeAllEnabled,
                CallModelDiagDumpEnabledForDev: form.advanced.callModelDiagDumpEnabledForDev,
                CTICallModelLogEnabled: form.advanced.ctiCallModelLogEnabled,
                UserNotificationsEnabled: form.advanced.userNotificationsEnabled,
                ExtensionsPerEnterpriseUser: form.advanced.extensionsPerEnterpriseUser,
                ...(hasTelsysSyncRestrictionPerms
                    ? {
                          MaximumAllowedUsersSync: form.advanced.maximumAllowedUsersSync,
                          MaximumAllowedUsersMonitored: form.advanced.maximumAllowedUsersMonitored
                      }
                    : {}),
                ScheduledReportsPerUser: form.advanced.scheduledReportsAllowedPerUser,
                EmailsPerScheduledReport: form.advanced.scheduledEmailsAllowedPerReport
            },
            LicenseCapManagement:
                form.communications.type === TelSysTypes.MS_TEAMS
                    ? {
                          EssentialsCap: IsStringEmpty(form.licenseManagement.essentials)
                              ? null
                              : Number(form.licenseManagement.essentials),
                          ProfessionalCap: IsStringEmpty(form.licenseManagement.professional)
                              ? null
                              : Number(form.licenseManagement.professional),
                          EnterpriseCap: IsStringEmpty(form.licenseManagement.enterprise)
                              ? null
                              : Number(form.licenseManagement.enterprise),
                          CallCentreAgentCap: IsStringEmpty(form.licenseManagement.callCentreAgent)
                              ? null
                              : Number(form.licenseManagement.callCentreAgent),
                          CallCentreSupervisorCap: IsStringEmpty(form.licenseManagement.callCentreSupervisor)
                              ? null
                              : Number(form.licenseManagement.callCentreSupervisor),
                          CXIntegrationCap: IsStringEmpty(form.licenseManagement.cxIntegration)
                              ? null
                              : Number(form.licenseManagement.cxIntegration)
                      }
                    : undefined
        })
    )
